import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { CancelButton } from "../Primitives/Buttons";
import { SaveIcon } from "../Primitives/Icons";
import { IPomReportingListItem } from "../../domain/reporting/pom-reporting";
import { getReportingPeriodLabel } from "../../utils/reporting-period-label.util";
import { useDownloadReportingExportMutation } from "../../repositories/queries/reportings/download-reporting-export.query";
import { PomReportingPeriodPicker } from "../reporting-period-picker/pom-reporting-period-picker.component";

interface IPomReportingExcelExportDialogProps {
  open: boolean;
  contractNo: string;
  reportingList: IPomReportingListItem[];
  onClose: () => void;
}

export const PomReportingExcelExportDialog: FC<IPomReportingExcelExportDialogProps> = (props) => {
  const { open, contractNo, reportingList, onClose } = props;
  const { t, i18n } = useTranslation();

  const [startReportingNo, setStartReportingNo] = useState<number | undefined>(undefined);
  const [endReportingNo, setEndReportingNo] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: downloadExport, isPending: isDownloading } = useDownloadReportingExportMutation();

  const sortedList = [...reportingList].sort((a, b) => a.reportingNo - b.reportingNo);

  const startItem = startReportingNo !== undefined ? reportingList.find((r) => r.reportingNo === startReportingNo) : undefined;
  const endItem = endReportingNo !== undefined ? reportingList.find((r) => r.reportingNo === endReportingNo) : startItem;

  const isValid = startReportingNo !== undefined;

  const handleClose = (): void => {
    setStartReportingNo(undefined);
    setEndReportingNo(undefined);
    setError(null);
    onClose();
  };

  const handleExport = async (): Promise<void> => {
    if (!isValid || startReportingNo === undefined) return;
    setError(null);

    try {
      const lang = i18n.language === "en" ? "en" : "de";
      const resolvedEnd = endReportingNo ?? startReportingNo;
      await downloadExport({ contractNo, startReportingNo, endReportingNo: resolvedEnd, lang });
      handleClose();
    } catch {
      setError(t("pom.reportings.export_dialog.error"));
    }
  };

  const handleRangeSelected = (startNo: number | undefined, endNo: number | undefined): void => {
    setStartReportingNo(startNo);
    setEndReportingNo(endNo);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("pom.reportings.export_dialog.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography>{t("pom.reportings.export_dialog.instruction")}</Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <PomReportingPeriodPicker
            mode="range"
            reportingList={sortedList}
            isLoading={false}
            draftReportingNos={[]}
            startReportingNo={startReportingNo}
            endReportingNo={endReportingNo}
            onRangeSelected={handleRangeSelected}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", px: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("pom.reportings.export_dialog.start_period")}:{" "}
              {startItem ? getReportingPeriodLabel(startItem.reportingPeriodStartDate, startItem.reportingPeriodEndDate) : "–"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("pom.reportings.export_dialog.end_period")}:{" "}
              {endItem ? getReportingPeriodLabel(endItem.reportingPeriodStartDate, endItem.reportingPeriodEndDate) : "–"}
            </Typography>
          </Box>

          {isDownloading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{t("pom.reportings.export_dialog.downloading")}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={handleClose} disabled={isDownloading}>
          {t("pom.reportings.export_dialog.cancel")}
        </CancelButton>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || isDownloading}
          onClick={() => void handleExport()}
          endIcon={<SaveIcon svgColor="white" />}
        >
          {t("pom.reportings.export_dialog.export_button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
